
import { useState, useMemo } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { ArrowTrendingUpIcon, ArrowTrendingDownIcon } from '@heroicons/react/24/outline';
import { cn } from '@/components/ui/utils';

interface DashboardChartsProps {
  stats: {
    revenue: number;
    newsletter: number;
    affiliates: number;
    bookings?: number;
  } | null;
  history?: {
    revenue: (string | { date: string; amount: number })[];
    newsletter: string[];
    affiliates: string[];
    bookings?: (string | { date: string; amount: number })[];
  };
}

type TimeRange = '1H' | '24H' | '7D' | '30D' | '60D' | '90D' | '1Y';

/** Length of each range, used for period-over-period comparison. */
const RANGE_MS: Record<TimeRange, number> = {
  '1H': 60 * 60 * 1000,
  '24H': 24 * 60 * 60 * 1000,
  '7D': 7 * 24 * 60 * 60 * 1000,
  '30D': 30 * 24 * 60 * 60 * 1000,
  '60D': 60 * 24 * 60 * 60 * 1000,
  '90D': 90 * 24 * 60 * 60 * 1000,
  '1Y': 365 * 24 * 60 * 60 * 1000,
};
type MetricType = 'revenue' | 'newsletter' | 'affiliates' | 'bookings';

export function DashboardCharts({ stats, history }: DashboardChartsProps) {
  const [timeRange, setTimeRange] = useState<TimeRange>('7D');
  const [metric, setMetric] = useState<MetricType>('revenue');

  const data = useMemo(() => {
    // If we have history but the requested metric history is empty, 
    // and we have a corresponding stats value, we should still show 0 or a flat line
    // rather than falling back to simulated "fake" data which is misleading.
    const hasAnyHistory = history && (history.revenue.length > 0 || history.newsletter.length > 0 || history.affiliates.length > 0 || (history.bookings?.length ?? 0) > 0);
    const metricHistory = (history?.[metric as keyof typeof history] || []) as (string | { date: string; amount: number })[];

    if (!history) {
      // Fallback to simulated data if no history object provided AT ALL
      const points = timeRange === '1H' ? 60 :
        timeRange === '24H' ? 24 :
          timeRange === '7D' ? 7 :
            timeRange === '30D' ? 30 :
              timeRange === '60D' ? 60 :
                timeRange === '90D' ? 90 : 12;

      const baseValue = metric === 'revenue' ? (stats?.revenue || 0) :
        metric === 'newsletter' ? (stats?.newsletter || 0) :
        metric === 'bookings' ? (stats?.bookings || 0) :
          (stats?.affiliates || 0);

      // If stats are 0, don't simulate growth
      if (baseValue === 0) {
        return Array.from({ length: points }).map((_, i) => ({
          name: i.toString(),
          value: 0
        }));
      }

      return Array.from({ length: points }).map((_, i) => {
        const progress = i / (points - 1);
        const randomVariation = (Math.random() - 0.5) * 0.1;
        const trend = baseValue * (0.7 + (progress * 0.3));
        return {
          name: i.toString(),
          value: Math.max(0, trend + (trend * randomVariation))
        };
      });
    }

    // Process actual history data.
    //
    // Two aggregation modes:
    //   - Cumulative (newsletter, affiliates): running total of subscribers/affiliates
    //     up to and including each bucket. The line should grow over time and
    //     reflect items registered before the visible window as a "starting balance."
    //   - Per-bucket (revenue, bookings): the amount generated WITHIN each bucket's
    //     time slot. A $300 invoice paid before the window must NOT appear on
    //     every day — it should only appear on the day it was actually paid.
    //
    // The previous implementation was cumulative for everything, which caused a
    // single past invoice to render as a flat line at $300 across the entire range.
    const isCountMetric = metric === 'newsletter' || metric === 'affiliates';
    const isCumulative = isCountMetric;
    void hasAnyHistory;

    const items = metricHistory.map(item => {
      if (typeof item === 'string') {
        return { time: new Date(item).getTime(), amount: isCountMetric ? 1 : 0 };
      }
      return { time: new Date(item.date).getTime(), amount: item.amount };
    }).sort((a, b) => a.time - b.time);

    const now = Date.now();
    let startTime = now;
    let interval = 0;
    let formatLabel = (d: Date) => d.toLocaleDateString();

    switch (timeRange) {
      case '1H':
        startTime = now - 60 * 60 * 1000;
        interval = 60 * 1000; // 1 min
        formatLabel = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        break;
      case '24H':
        startTime = now - 24 * 60 * 60 * 1000;
        interval = 60 * 60 * 1000; // 1 hour
        formatLabel = (d) => d.toLocaleDateString([], { weekday: 'short' }) + ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        break;
      case '7D':
        startTime = now - 7 * 24 * 60 * 60 * 1000;
        interval = 24 * 60 * 60 * 1000; // 1 day
        formatLabel = (d) => d.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
        break;
      case '30D':
        startTime = now - 30 * 24 * 60 * 60 * 1000;
        interval = 24 * 60 * 60 * 1000; // 1 day
        formatLabel = (d) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        break;
      case '60D':
        startTime = now - 60 * 24 * 60 * 60 * 1000;
        interval = 24 * 60 * 60 * 1000; // 1 day
        formatLabel = (d) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        break;
      case '90D':
        startTime = now - 90 * 24 * 60 * 60 * 1000;
        interval = 24 * 60 * 60 * 1000; // 1 day
        formatLabel = (d) => d.toLocaleDateString([], { month: 'short', day: 'numeric' });
        break;
      case '1Y':
        startTime = now - 365 * 24 * 60 * 60 * 1000;
        interval = 30 * 24 * 60 * 60 * 1000; // ~1 month
        formatLabel = (d) => d.toLocaleDateString([], { month: 'short', year: '2-digit', day: 'numeric' });
        break;
    }

    // Generate buckets
    const buckets: { time: number; label: string }[] = [];
    for (let t = startTime; t <= now; t += interval) {
      buckets.push({
        time: t,
        label: formatLabel(new Date(t))
      });
    }

    // Ensure the last bucket reaches current time so the final data point is accurate
    const lastBucket = buckets[buckets.length - 1];
    if (!lastBucket || now - lastBucket.time > interval * 0.1) {
      buckets.push({
        time: now,
        label: formatLabel(new Date(now))
      });
    } else {
      // Snap the last bucket to now so it captures all current data
      lastBucket.time = now;
    }

    // Calculate values per bucket.
    //
    // Cumulative (newsletter / affiliates): running total of items with time <= bucket.time.
    //   This intentionally includes items registered before the visible window so the
    //   subscriber count line reflects the true running total.
    //
    // Per-bucket (revenue / bookings): only items whose time falls inside this bucket's
    //   slot [bucket.time, nextBucket.time). Items outside the window contribute nothing,
    //   so a single past invoice does not bleed across every day of the chart.
    return buckets.map((bucket, idx) => {
      let value: number;
      if (isCumulative) {
        value = items
          .filter(item => item.time <= bucket.time)
          .reduce((sum, item) => sum + item.amount, 0);
      } else {
        const nextTime = buckets[idx + 1]?.time ?? Infinity;
        value = items
          .filter(item => item.time >= bucket.time && item.time < nextTime)
          .reduce((sum, item) => sum + item.amount, 0);
      }

      return {
        name: bucket.label,
        value: value,
      };
    });

  }, [timeRange, metric, stats, history]);

  // For per-bucket $ metrics, the "current value" headline above the chart should be
  // the total across all visible buckets (i.e. revenue/bookings generated in the
  // selected window) — not just the last bucket, which is usually $0. For cumulative
  // count metrics it's still the latest running total, which is the last bucket's value.
  const isCumulativeMetric = metric === 'newsletter' || metric === 'affiliates';
  const headlineValue = isCumulativeMetric
    ? (data.length > 0 ? (data[data.length - 1]?.value ?? 0) : 0)
    : data.reduce((sum, d) => sum + (d?.value ?? 0), 0);

  const config = {
    revenue: { color: '#4ade80', label: 'Revenue', prefix: '$' },
    newsletter: { color: '#60a5fa', label: 'Newsletter', prefix: '' },
    affiliates: { color: '#c084fc', label: 'Affiliates', prefix: '' },
    bookings: { color: '#f59e0b', label: 'Bookings', prefix: '$' },
  };

  // Period-over-period change for the headline, matching the hero tracker:
  // this window against the one immediately before it, same length. Cumulative
  // count metrics compare running totals; per-bucket $ metrics compare sums.
  const periodChange = useMemo(() => {
    const metricHistory = history?.[metric];
    if (!metricHistory || metricHistory.length === 0) return null;

    const span = RANGE_MS[timeRange];
    const now = Date.now();
    const isCount = metric === 'newsletter' || metric === 'affiliates';

    const points = metricHistory.map(item =>
      typeof item === 'string'
        ? { time: new Date(item).getTime(), amount: isCount ? 1 : 0 }
        : { time: new Date(item.date).getTime(), amount: item.amount },
    );

    const sumBefore = (t: number) =>
      points.filter(p => p.time <= t).reduce((s, p) => s + p.amount, 0);
    const sumBetween = (a: number, b: number) =>
      points.filter(p => p.time >= a && p.time < b).reduce((s, p) => s + p.amount, 0);

    const current = isCount ? sumBefore(now) : sumBetween(now - span, now);
    const previous = isCount ? sumBefore(now - span) : sumBetween(now - 2 * span, now - span);

    if (previous === 0) {
      if (current === 0) return null;
      // No baseline to divide by — report the gain itself, as the hero does.
      return { pct: current, up: true };
    }
    const pct = ((current - previous) / previous) * 100;
    return { pct, up: pct >= 0 };
  }, [history, metric, timeRange]);

  return (
    <div className="w-full h-full flex flex-col">
      {/* Current Value Display */}
      <div className="flex items-center justify-center gap-4 mb-4">
        <div className="flex items-center justify-center gap-2 text-center">
          <span className="text-2xl font-bold font-mono" style={{ color: config[metric].color }}>
            {config[metric].prefix}{headlineValue.toFixed(0)}
          </span>
          <span className="text-xs text-white/40 uppercase">{config[metric].label}</span>
          {periodChange && (
            <span
              className={`flex items-center gap-0.5 text-xs font-bold font-mono ${
                periodChange.up ? 'text-green-400' : 'text-red-400'
              }`}
              title={`vs previous ${timeRange}`}
            >
              {periodChange.up ? (
                <ArrowTrendingUpIcon className="w-3.5 h-3.5" />
              ) : (
                <ArrowTrendingDownIcon className="w-3.5 h-3.5" />
              )}
              {periodChange.up ? '+' : ''}{periodChange.pct.toFixed(0)}%
            </span>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0 relative mb-4">
        <div className="absolute inset-0">
          <ResponsiveContainer width="100%" height="100%" minWidth={100} minHeight={100} debounce={200}>
            <AreaChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" vertical={false} />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }}
                interval="preserveStartEnd"
                minTickGap={40}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 9 }}
                width={35}
                tickFormatter={(value) => `${config[metric].prefix}${value >= 1000 ? `${(value / 1000).toFixed(0)}k` : value.toFixed(0)}`}
                domain={['auto', 'auto']}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#000',
                  border: 'none',
                  borderRadius: 0,
                  fontSize: '12px',
                  padding: '8px 12px'
                }}
                itemStyle={{ color: config[metric].color }}
                formatter={(value: number | undefined) => [
                  `${config[metric].prefix}${(value ?? 0).toFixed(0)}`,
                  config[metric].label
                ]}
                labelStyle={{ display: 'block', color: '#888', marginBottom: '4px', fontSize: '10px' }}
              />
              <Area
                type="monotone"
                dataKey="value"
                stroke={config[metric].color}
                fill={`${config[metric].color}20`}
                strokeWidth={2}
                dot={{ r: 2, fill: config[metric].color, strokeWidth: 0 }}
                activeDot={{ r: 4, fill: config[metric].color, strokeWidth: 2, stroke: '#000' }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Controls Container */}
      <div className="flex flex-col gap-3">
        {/* Time Range Toggle - Full Width */}
        <div className="flex w-full bg-white/5">
          {(['1H', '24H', '7D', '30D', '60D', '90D', '1Y'] as TimeRange[]).map((t) => (
            <button
              key={t}
              onClick={() => setTimeRange(t)}
              className={cn(
                "flex-1 py-3 text-[10px] md:text-xs uppercase tracking-wider font-medium transition-colors",
                timeRange === t
                  ? "bg-white text-black font-bold"
                  : "text-white/40 hover:text-white hover:bg-white/10"
              )}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Metric Toggle - Full Width */}
        <div className="flex w-full bg-white/5">
          {(['revenue', 'newsletter', 'affiliates', 'bookings'] as MetricType[]).map((m) => (
            <button
              key={m}
              onClick={() => setMetric(m)}
              className={cn(
                "flex-1 py-3 text-[10px] md:text-xs uppercase tracking-widest font-bold transition-colors",
                metric === m
                  ? "bg-white text-black"
                  : "text-white/40 hover:text-white hover:bg-white/10"
              )}
            >
              {config[m].label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}




